'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import PageType from '@alltrails/amplitude/enums/PageType';
import logDirectionsButtonTapped from '@alltrails/amplitude/events/logDirectionsButtonTapped';
import useUser from '@alltrails/context/hooks/useUser';
import IconButton from '@alltrails/denali/components/IconButton';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import Car from '@alltrails/denali/icons/Car';
import Share from '@alltrails/denali/icons/Share';
import useNavigateBackInternal from '@alltrails/layout/hooks/useNavigateBackInternal';
import type { ShareObject } from '@alltrails/sharing/types/ShareObject';
import { useModal } from '../../ShareModal/ShareModalContext';
import styles from './MobileButtons.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type Props = {
  directionsLink?: string;
  shareObject: ShareObject;
  id: number;
  pageType?: PageType;
};

const MobileButtons = ({ directionsLink, shareObject, id, pageType }: Props) => {
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const { openModal } = useModal();
  const intl = useIntl();
  const user = useUser();
  const { canNavigateBack, navigateBack } = useNavigateBackInternal();

  return (
    <div className={styles.mobileButtonsContainer}>
      <IconButton
        icon={{ Component: ArrowSq, orientation: 'left' }}
        onClick={navigateBack}
        title={canNavigateBack ? intl.formatMessage({ defaultMessage: 'Back' }) : intl.formatMessage({ defaultMessage: 'Back to Explore' })}
        variant="transparent"
        size="sm"
        className={styles.button}
      />
      <div className={styles.rightButtonsContainer}>
        {directionsLink && (
          <IconButton
            icon={{ Component: Car }}
            {...(user ? { linkInfo: { href: directionsLink, target: '_blank', rel: ['noopener', 'noreferrer'] } } : {})}
            onClick={() => {
              logDirectionsButtonTapped({ id, page_type: pageType });

              if (!user) {
                setIsSignUpModalOpen(true);
              }
            }}
            title={intl.formatMessage({ defaultMessage: 'Directions' })}
            variant="transparent"
            size="sm"
            className={styles.button}
          />
        )}
        {shareObject && (
          <IconButton
            icon={{ Component: Share }}
            onClick={openModal}
            title={intl.formatMessage({ defaultMessage: 'Share' })}
            variant="transparent"
            size="sm"
            className={styles.button}
          />
        )}
      </div>
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: id, pageType: 'area' }}
          isOpen={isSignUpModalOpen}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            window.open(directionsLink, '_blank', 'noopener noreferrer');
          }}
          trigger="Location Directions"
          upsellTrigger="Location Directions"
        />
      )}
    </div>
  );
};

export default MobileButtons;
